import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { displaySalesperson } from "../utils/salespeople";
import ConfirmModal from "./ConfirmModal";

/**
 * components/SalespersonPicker.js
 * Searchable picker for choosing an EXISTING salesperson — deliberately mirrors
 * `ContactPicker.js` almost line for line (Phase T1e's "reuse the existing contact-picker
 * pattern rather than inventing a new one"). Salespeople are PICKED, not typed free-text, same
 * as contacts.
 *
 * Props: { value: salespersonId|null, salespeople=[], persons=[], onChange(salespersonId|null),
 *          onRequestCreate(name), locationLabel, locationBranchId, onCreateSalespersonRole }
 *
 * Person/Role restructure, Stage 2: a role has no name of its own — every name shown here
 * (option labels, the selected-value display, the "already exists" exact-match check) now
 * resolves through the linked person via `displaySalesperson`/`resolveSalespersonName`, which
 * is why this component needs the `persons` prop.
 *
 * **Stage 3 batch 3a: this picker no longer creates anything itself.** Through batch 2, `create()`
 * called `onCreateSalesperson(name)` and awaited an id back, exactly like `ContactPicker`'s own
 * `create()` awaits `onCreateContact`. That stopped being possible once a role needed a WHO
 * (a person, possibly one of several sharing this exact name) before it could have a WHERE —
 * see `SalespersonFormModal.js`'s own header comment for the full two-stage design this
 * component now defers to entirely. `create()` here is now a one-way notification: it tells the
 * host (`TransactionFormModal`) "the user wants to create a new salesperson, starting from this
 * typed name," and gets out of the way — no id, no `onChange` call, no selection. If the host's
 * own flow succeeds, IT calls `onChange` from the outside once it has a real new id; if the user
 * cancels that flow, nothing here changes at all. This is also why `create()` still closes the
 * dropdown (`commitClose()`) even though it isn't committing a selection — a full-screen modal
 * is about to render on top of this picker either way.
 *
 * **`salespeople` is LOCATION-scoped by the host (Person/Role restructure, Stage 3 batch 4/5
 * fix)** — `TransactionFormModal` passes only the roles valid for the anchor currently being
 * edited (Pusat roles for a Pusat anchor, a specific branch's roles for a Cabang anchor), not
 * every role a person holds everywhere. See `TransactionFormModal.js`'s own `filteredSalespeople`
 * for the filter itself.
 *
 * **The add-role offer (same fix) closes the gap that filtering creates.** A person who holds a
 * role somewhere else but NOT at this location no longer appears in `salespeople` at all — with
 * no location filtering, they'd have shown up as an ordinary option; filtered, they simply
 * vanish, with no way to say "it's the same person, just add them here." So: any `persons` entry
 * whose name matches the typed query but has NO entry in the (already location-scoped)
 * `salespeople` prop renders as its own row, "{name} ({code}) — belum ada peran di
 * {locationLabel}", offered ALONGSIDE the ordinary role results. Picking it does NOT select or
 * create anything immediately — it opens a brief `ConfirmModal` ("{name} belum punya peran di
 * {locationLabel}. Tambahkan?") stating plainly what's about to happen, matching this app's own
 * "state the consequence before acting" convention (see `MoveSalespersonModal`'s identical
 * approach for Stage 5's own move action). Confirming calls `onCreateSalespersonRole({personId,
 * branchId: locationBranchId})` — the EXACT SAME function `KelolaSalesModal`'s "+ Tambah Sales"
 * and this component's own "+ Tambah sales baru…" quick-add already use (App.js's
 * `createSalespersonRole`, which in turn calls `utils/salespeople.js`'s `addSalesperson`) — so
 * this can never mint a second person (no `name`/`code` in the payload, just `personId`) and
 * inherits Part B's own one-role-per-person-per-location logic for free: if the person happens
 * to hold an INACTIVE role at this exact location already (invisible to this picker, since
 * `salespeople` is active-filtered), `addSalesperson` REACTIVATES that record instead of
 * creating a second one — no special-casing needed here, the shared write path already does the
 * right thing. A real new/reactivated id selects immediately (`onChange`) and closes the
 * confirm; cancelling leaves the form completely untouched, nothing created.
 * **Because a person who already has a name-exact match anywhere is offered THIS way, `exact`
 * (which suppresses "+ Tambah sales baru…", below) is keyed on the full `persons` list, not just
 * the location-scoped `salespeople` — offering "create a brand-new person" for a name that
 * already belongs to someone would risk minting a duplicate person when "add a role for the
 * existing one" is what's actually needed.**
 *
 * Not a thin `Combobox` wrapper for the same reason `ContactPicker` isn't: `value` here is a
 * `generateId()`-produced id, not the typed name, so `Combobox`'s own `onCreate(text);
 * onChange(text)` flow would select the raw text as the "salespersonId" — moot for creation now
 * (this picker doesn't create), but still true for `choose()`, which is why this stays its own
 * component rather than becoming a bare `Combobox` wrapper.
 *
 * The same Esc-cascade / busy-Escape contract as `ContactPicker` (task 5e) applies here too:
 * Escape only clears+stops while "busy" (menu open, or unresolved typed text) so a single
 * Escape inside this picker never also closes the host modal in the same keypress.
 *
 * **`pinnedAddRoleCandidates` (contacts/salespeople link-up, Part 2)**: normally a person only
 * shows up in the "belum ada peran di ..." add-role offer once the user types a matching name
 * (`addRoleCandidates`, above) — but a host that already knows exactly which person needs this
 * offer (e.g. `TransactionFormModal` resolving a picked Contact's own linked people against the
 * transaction's location) needs it visible WITHOUT requiring the user to type first — "never
 * hold it invisibly." Pinned persons render as their own always-visible rows directly under the
 * input, reusing the EXACT SAME `requestAddRole`/`confirmAddRole` flow (the same `ConfirmModal`,
 * the same `onCreateSalespersonRole` call) as the query-driven offer — this is additive, not a
 * second mechanism. Deduplicated against the query-driven list by id so a pinned person who also
 * happens to match the typed text isn't offered twice.
 *
 * **Design fix (manual-testing report): a pinned candidate must never read as a dead end.**
 * When exactly one is pending and nothing real is selected, the input ITSELF shows that
 * person's name, greyed/italic (`combobox__input--unresolved`) — visibly "this is what's
 * decided," per the original autofill intent — but this is DISPLAY ONLY, never a real `value`;
 * `onChange` is never called for it. Each pinned row renders the SAME greyed name paired with
 * an explicit, adjacent action button, "Tambahkan peran di {locationLabel}" — so the "decided
 * but unusable" name always has something real beside it to click, not nothing. Clicking it
 * runs the identical confirm flow; a successful add turns the greyed input into a normal, real
 * selection (`onChange(newId)`), exactly the "becomes a real selection" contract asked for.
 * Whether this half-decided state can even reach Simpan is the HOST's job — this component only
 * ever reports a real `value` once one exists; see `TransactionFormModal.js`'s own
 * `contactRoleNarrowing`/`salesHint` wiring for the save-time block + reason.
 *
 * **`foreignBranchNote(s)` (bug fix — the pinned-role trap)**: `salespeople` always includes the
 * host's own "keep the current selection visible" exception (`TransactionFormModal.js`'s
 * `isCurrentRole`) even once it no longer matches whatever location this picker is actually
 * scoped to right now — e.g. the host's own branch filter moved to AMIN but the transaction's
 * stored role is still TMM's. Before this fix, that pinned option rendered with the exact same
 * plain `displaySalesperson(...)` text as a real AMIN option, so picking it read as "picking an
 * AMIN salesperson" when it silently wasn't. Optional, defaults to a no-op (`() => null`) — when
 * it returns a non-null string for a given role, that string is appended after the normal label
 * (same suffix convention `displaySalesperson`'s own `" · Nonaktif"` already uses), on BOTH the
 * closed-field display and the option row, so the mismatch is visible wherever that role's name
 * appears, not just in the dropdown.
 */
export default function SalespersonPicker({
  value, salespeople = [], persons = [], onChange, onRequestCreate,
  locationLabel = "lokasi ini", locationBranchId = null, onCreateSalespersonRole,
  pinnedAddRoleCandidates = [], foreignBranchNote = () => null, id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [pendingAddRole, setPendingAddRole] = useState(null); // a person object, while the confirm is open
  const [addRoleError, setAddRoleError] = useState(null);
  const wrapRef = useRef(null);
  const listboxId = useId();

  const selected = useMemo(() => salespeople.find((s) => s.id === value) || null, [salespeople, value]);

  // Appends foreignBranchNote's own suffix (or nothing) after the normal display text — the
  // single place every rendered label for a REAL role (not a pinned-add-role offer, which has
  // its own separate "belum ada peran" text) goes through, so the closed-field display and each
  // option row can never disagree about whether a given role gets the note.
  const labelFor = (s) => displaySalesperson(persons, s) + (foreignBranchNote(s) || "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return salespeople;
    return salespeople.filter((s) => displaySalesperson(persons, s).toLowerCase().includes(q));
  }, [salespeople, persons, query]);

  const trimmed = query.trim();

  // Every person matching the typed text who has NO role at all in the (already location-
  // scoped) `salespeople` prop — see this file's own header comment for the full design.
  // Excludes anyone already offered via `pinnedAddRoleCandidates` (below) so a pinned person who
  // also happens to match the typed text isn't shown twice, once pinned and once query-driven.
  const addRoleCandidates = useMemo(() => {
    if (!trimmed) return [];
    const q = trimmed.toLowerCase();
    const rolePersonIds = new Set(salespeople.map((s) => s.personId));
    const pinnedIds = new Set(pinnedAddRoleCandidates.map((p) => p.id));
    return persons.filter((p) => p.name.toLowerCase().includes(q) && !rolePersonIds.has(p.id) && !pinnedIds.has(p.id));
  }, [trimmed, persons, salespeople, pinnedAddRoleCandidates]);

  // A person-level exact match (not just a role visible HERE) suppresses "+ Tambah sales baru…"
  // — see this file's own header comment for why.
  const exact = !!trimmed && persons.some((p) => p.name.toLowerCase() === trimmed.toLowerCase());
  const showCreate = trimmed.length > 0 && !exact;
  const totalRows = filtered.length + addRoleCandidates.length + (showCreate ? 1 : 0);

  const commitClose = () => { setOpen(false); setQuery(""); setActive(0); };
  const dismiss = () => { setOpen(false); setActive(0); };
  const abandon = () => { setOpen(false); setQuery(""); setActive(0); };

  const choose = (s) => { onChange(s.id); commitClose(); };
  const create = () => {
    if (!trimmed) return;
    onRequestCreate(trimmed);
    commitClose();
  };
  const requestAddRole = (person) => {
    setAddRoleError(null);
    setPendingAddRole(person);
    commitClose();
  };
  const confirmAddRole = () => {
    const newId = onCreateSalespersonRole({ personId: pendingAddRole.id, branchId: locationBranchId });
    if (newId) {
      setPendingAddRole(null);
      onChange(newId);
    } else {
      setAddRoleError(`Peran untuk ${pendingAddRole.name} tidak dapat ditambahkan.`);
      setPendingAddRole(null);
    }
  };

  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) dismiss(); };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, totalRows - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active < filtered.length) { if (filtered[active]) choose(filtered[active]); }
      else if (active < filtered.length + addRoleCandidates.length) {
        const person = addRoleCandidates[active - filtered.length];
        if (person) requestAddRole(person);
      } else if (showCreate && active === filtered.length + addRoleCandidates.length) create();
    } else if (e.key === "Escape") {
      if (open || showCreate) { abandon(); e.stopPropagation(); }
    }
  };

  // Design fix: while nothing real is selected and exactly one pinned candidate is pending,
  // show that person's name in the input itself, greyed — visible as "this is what's decided,"
  // never a real value (see this file's header comment). Query/create still take priority, same
  // as `selected` already does, since the user actively typing something always wins.
  const unresolvedSingle = !value && pinnedAddRoleCandidates.length === 1 ? pinnedAddRoleCandidates[0] : null;
  const inputValue = open ? query
    : showCreate ? query
    : selected ? labelFor(selected)
    : unresolvedSingle ? `${unresolvedSingle.name}${unresolvedSingle.code ? ` (${unresolvedSingle.code})` : ""}`
    : "";
  const showUnresolvedHint = !open && showCreate;

  return (
    <>
    <div className="combobox" ref={wrapRef}>
      <input
        id={id}
        className={"form-input combobox__input" + (!open && !selected && unresolvedSingle ? " combobox__input--unresolved" : "")}
        role="combobox" aria-expanded={open} aria-controls={listboxId}
        placeholder="Cari atau tambah sales…" value={inputValue}
        onFocus={() => setOpen(true)}
        // Same fix as ContactPicker.js's own identical bug: opening on `onFocus` alone means the
        // menu can never reopen once this input already holds DOM focus without blurring — the
        // exact state right after picking a role, clearing via "×", or a Lokasi/Arah toggle
        // clearing the value programmatically (none of these blur the input). `onMouseDown`
        // fires on every click regardless of prior focus state.
        onMouseDown={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
      />
      {value && !open && (
        <button type="button" className="combobox__clear" title="Kosongkan"
          onMouseDown={(e) => { e.preventDefault(); onChange(null); }}>×</button>
      )}
      {open && (
        <div className="combobox__menu" role="listbox" id={listboxId}>
          {filtered.length === 0 && addRoleCandidates.length === 0 && !showCreate && <div className="combobox__empty">Tidak ada hasil.</div>}
          {filtered.map((s, i) => (
            <div key={s.id} role="option" aria-selected={s.id === value}
              className={"combobox__option" + (i === active ? " combobox__option--active" : "")}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}>
              <div className="combobox__option-primary">{labelFor(s)}</div>
            </div>
          ))}
          {addRoleCandidates.map((p, i) => {
            const idx = filtered.length + i;
            return (
              <div key={p.id} role="option" aria-selected={false}
                className={"combobox__option" + (idx === active ? " combobox__option--active" : "")}
                onMouseEnter={() => setActive(idx)}
                onMouseDown={(e) => { e.preventDefault(); requestAddRole(p); }}>
                <div className="combobox__option-primary">{p.name} ({p.code}) — belum ada peran di {locationLabel}</div>
              </div>
            );
          })}
          {showCreate && (
            <div role="option" aria-selected={false}
              className={"combobox__option" + (active === filtered.length + addRoleCandidates.length ? " combobox__option--active" : "")}
              onMouseEnter={() => setActive(filtered.length + addRoleCandidates.length)}
              onMouseDown={(e) => { e.preventDefault(); create(); }}>
              <div className="combobox__create">+ Tambah sales baru: "{trimmed}"</div>
            </div>
          )}
        </div>
      )}
    </div>
    {pinnedAddRoleCandidates.length > 0 && (
      <div className="combobox__pinned-add-role">
        {pinnedAddRoleCandidates.map((p) => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", marginTop: "var(--gap-xs)" }}>
            <span style={{ color: "var(--muted)", fontStyle: "italic" }}>
              {p.name}{p.code ? ` (${p.code})` : ""}
            </span>
            <button type="button" className="btn btn-sm" onClick={() => requestAddRole(p)}>
              Tambahkan peran di {locationLabel}
            </button>
          </div>
        ))}
      </div>
    )}
    {showUnresolvedHint && (
      <p className="form-hint" style={{ color: "var(--red)" }}>
        Teks belum dipilih — klik "+ Tambah sales baru" untuk menyimpannya.
      </p>
    )}
    {addRoleError && (
      <p className="form-hint" style={{ color: "var(--red)" }}>{addRoleError}</p>
    )}
    {pendingAddRole && (
      <ConfirmModal
        title="Tambahkan peran?"
        message={`${pendingAddRole.name} belum punya peran di ${locationLabel}. Tambahkan?`}
        confirmLabel="Tambahkan"
        danger={false}
        onConfirm={confirmAddRole}
        onClose={() => setPendingAddRole(null)}
      />
    )}
    </>
  );
}
